/**
 * User Server Functions
 *
 * This module provides TanStack Start server functions for user-related
 * API calls following the BFF (Backend for Frontend) pattern.
 */

import { createServerFn } from "@tanstack/react-start";
import type { User } from "@/lib/api/generated";
import {
  getUserProfileApiUsersProfileGet,
  updateUserProfileApiUsersProfilePatch,
} from "@/lib/api/generated/users/users";
import { logError, logInfo } from "@/lib/structured-log";

/**
 * Get current user profile
 *
 * Returns the authenticated user's profile information including:
 * - id: Clerk user ID
 * - email: User's email address
 * - username: Unique handle
 * - first_name: User's first name
 * - last_name: User's last name
 * - image_url: URL to user's profile image (provided by Clerk)
 *
 * @returns User profile data
 * @throws Error if the API request fails
 */
export const getUserProfileServer = createServerFn({ method: "GET" }).handler(
  async (): Promise<User> => {
    try {
      const response = await getUserProfileApiUsersProfileGet();

      // Check response status
      if (response.status !== 200) {
        logError("users.profile.get_failed", {
          status: response.status,
        });
        throw new Error(`Failed to get user profile: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      logError("users.profile.error", {
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  },
);

/**
 * Update current user profile
 *
 * Updates the authenticated user's profile information.
 * Currently supports updating:
 * - full_name: User's full name (will be split into first_name and last_name)
 *
 * The updated data is synchronized to Clerk and will be reflected
 * in the local database on the next request.
 *
 * @param data - Profile update payload
 * @returns Updated user profile data
 * @throws Error if the API request fails
 */
export const updateUserProfileServer = createServerFn({ method: "POST" })
  .validator((data: { full_name?: string; language?: string; timezone?: string }) => data)
  .handler(async ({ data }): Promise<User> => {
    try {
      const response = await updateUserProfileApiUsersProfilePatch({
        full_name: data.full_name,
        language: data.language,
        timezone: data.timezone,
      });

      // Check response status
      if (response.status !== 200) {
        logError("users.profile.update_failed", {
          status: response.status,
          full_name: data.full_name,
        });
        throw new Error(`Failed to update user profile: ${response.status}`);
      }

      logInfo("users.profile.updated", {
        full_name: data.full_name,
        language: data.language,
        timezone: data.timezone,
      });

      return response.data;
    } catch (error) {
      logError("users.profile.update_error", {
        error: error instanceof Error ? error.message : "unknown-error",
        full_name: data.full_name,
      });
      throw error;
    }
  });
